import { Plus, X, RefreshCw, Clock, HelpCircle, Check, XCircle, Image as ImageIcon, LifeBuoy, ArrowUp } from 'lucide-react';
import type { Feedback, FeedbackStatus } from '../../api/client';

const STATUS_CONFIG: Record<FeedbackStatus, { label: string; icon: typeof Check; color: string; dotColor: string }> = {
  OPEN: { label: 'Offen', icon: Clock, color: 'text-blue-600 bg-blue-50', dotColor: 'bg-blue-500' },
  RUCKFRAGE: { label: 'Rueckfrage', icon: HelpCircle, color: 'text-amber-600 bg-amber-50', dotColor: 'bg-amber-500' },
  DONE: { label: 'Erledigt', icon: Check, color: 'text-green-600 bg-green-50', dotColor: 'bg-green-500' },
  REJECTED: { label: 'Abgelehnt', icon: XCircle, color: 'text-slate-500 bg-slate-100', dotColor: 'bg-slate-400' },
};

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `vor ${days} Tag${days !== 1 ? 'en' : ''}`;
  return new Date(dateStr).toLocaleDateString('de-DE');
}

interface TicketListProps {
  feedbacks: Feedback[];
  loading: boolean;
  onOpenTicket: (id: string) => void;
  onNewTicket: () => void;
  onClose: () => void;
  onRefresh: () => void;
}

export default function TicketList({
  feedbacks,
  loading,
  onOpenTicket,
  onNewTicket,
  onClose,
  onRefresh,
}: TicketListProps) {
  const ruckfrageCount = feedbacks.filter((f) => f.status === 'RUCKFRAGE').length;
  const openCount = feedbacks.filter((f) => f.status === 'OPEN').length;

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50 shrink-0">
        <div>
          <h2 className="font-semibold text-slate-800">Meldungen</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            {feedbacks.length} gesamt
            {openCount > 0 && <span className="ml-1.5 text-blue-600">{openCount} offen</span>}
            {ruckfrageCount > 0 && <span className="ml-1.5 text-amber-600">{ruckfrageCount} Rueckfrage</span>}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded transition-colors"
            title="Aktualisieren"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 rounded transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* New Ticket Button */}
      <div className="px-4 py-3 border-b shrink-0">
        <button
          onClick={onNewTicket}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Neue Meldung
        </button>
      </div>

      {/* Ticket List */}
      <div className="flex-1 overflow-y-auto">
        {loading && feedbacks.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Laden...</div>
        ) : feedbacks.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <LifeBuoy className="w-12 h-12 text-slate-200 mb-3" />
            <h3 className="text-sm font-semibold text-slate-600 mb-1">Alles in Ordnung?</h3>
            <p className="text-xs text-slate-400 leading-relaxed max-w-[260px]">
              Wenn etwas nicht funktioniert oder du eine Idee hast, klicke oben auf <strong className="text-slate-600">"+ Neue Meldung"</strong>. Wir helfen dir dann direkt hier.
            </p>
            <ArrowUp className="w-4 h-4 text-slate-300 mt-3 animate-bounce" />
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {feedbacks.map((fb) => {
              const cfg = STATUS_CONFIG[fb.status];
              const StatusIcon = cfg.icon;
              const lastComment = fb.comments.filter((c) => c.type === 'MESSAGE').slice(-1)[0];
              const lastActivity = lastComment?.createdAt || fb.updatedAt;
              const hasImages = fb.comments.some((c) => c.imagePath);
              const isDone = fb.status === 'DONE' || fb.status === 'REJECTED';

              return (
                <button
                  key={fb.id}
                  onClick={() => onOpenTicket(fb.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${isDone ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Status Dot */}
                    <div className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${cfg.dotColor}`} />

                    <div className="flex-1 min-w-0">
                      {/* Title */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate">{fb.title}</span>
                        {hasImages && <ImageIcon className="w-3 h-3 text-slate-400 shrink-0" />}
                      </div>

                      {/* Meta line */}
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-400">
                        <span className="text-[11px] font-mono">#{fb.number}</span>
                        <span className="text-slate-300">·</span>
                        <div className={`flex items-center gap-0.5 ${cfg.color} px-1 py-0.5 rounded text-[11px] font-medium`}>
                          <StatusIcon className="w-3 h-3" />
                          {cfg.label}
                        </div>
                        <span className="text-slate-300">·</span>
                        <span>{timeAgo(lastActivity)}</span>
                        {fb.comments.filter((c) => c.type === 'MESSAGE').length > 0 && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span>{fb.comments.filter((c) => c.type === 'MESSAGE').length} Nachricht{fb.comments.filter((c) => c.type === 'MESSAGE').length !== 1 ? 'en' : ''}</span>
                          </>
                        )}
                      </div>

                      {/* Last message preview */}
                      {lastComment?.message && (
                        <p className="text-xs text-slate-500 mt-1 truncate leading-relaxed">
                          {lastComment.author?.displayName ? `${lastComment.author.displayName}: ` : ''}{lastComment.message}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
